import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Button } from './ui/button';

interface ResizablePanelsProps {
    leftPanel: React.ReactNode;
    rightPanel: React.ReactNode;
    defaultLeftWidth?: number; // Percentage (0-100)
    minLeftWidth?: number; // Percentage
    maxLeftWidth?: number; // Percentage
    className?: string;
    height?: string;
    isRightPanelMinimized?: boolean;
    stickyRightPanel?: boolean; // New prop for sticky PDF behavior
    stickyTopOffset?: number; // New prop for top offset in pixels
    onToggleRightPanel?: () => void; // New prop for handling minimize/maximize
    // NEW: bottom offset so panels don't abut page bottom
    stickyBottomOffset?: number; // Bottom offset in pixels
}

export const ResizablePanels: React.FC<ResizablePanelsProps> = ({
    leftPanel,
    rightPanel,
    defaultLeftWidth = 50, // Default to 50% for table, 50% for PDF (was 60%/40%)
    minLeftWidth = 30,
    maxLeftWidth = 80,
    className = '',
    height = 'auto',
    isRightPanelMinimized = false,
    stickyRightPanel = true, // Default to true for better UX
    stickyTopOffset = 120, // Default offset to align with content headers
    onToggleRightPanel,
    // NEW default bottom offset
    stickyBottomOffset = 32,
}) => {
    const [leftWidth, setLeftWidth] = useState(defaultLeftWidth);
    const [isDragging, setIsDragging] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const dragStartX = useRef<number>(0);
    const dragStartWidth = useRef<number>(0);

    // Adjust layout when right panel is minimized
    const effectiveLeftWidth = isRightPanelMinimized ? 100 : leftWidth;
    const effectiveRightWidth = isRightPanelMinimized ? 0 : (100 - leftWidth);
    const showResizer = !isRightPanelMinimized;

    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        setIsDragging(true);
        dragStartX.current = e.clientX;
        dragStartWidth.current = leftWidth;
        
        // Prevent text selection during drag
        document.body.style.userSelect = 'none';
        document.body.style.cursor = 'col-resize';
    }, [leftWidth]);

    const handleMouseMove = useCallback((e: MouseEvent) => {
        if (!isDragging || !containerRef.current) return;

        const containerRect = containerRef.current.getBoundingClientRect();
        const containerWidth = containerRect.width;
        const deltaX = e.clientX - dragStartX.current;
        const deltaPercent = (deltaX / containerWidth) * 100;
        
        const newLeftWidth = Math.min(
            maxLeftWidth,
            Math.max(minLeftWidth, dragStartWidth.current + deltaPercent)
        );
        
        setLeftWidth(newLeftWidth);
    }, [isDragging, minLeftWidth, maxLeftWidth]);

    const handleMouseUp = useCallback(() => {
        setIsDragging(false);
        document.body.style.userSelect = '';
        document.body.style.cursor = '';
    }, []);

    const handleTogglePanel = () => {
        if (onToggleRightPanel) {
            onToggleRightPanel();
        }
    };

    // Add global mouse event listeners when dragging
    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            
            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [isDragging, handleMouseMove, handleMouseUp]);

    // Sticky layout with improved positioning
    if (stickyRightPanel) {
        // Use bottom offset so panel and resizer sit above the bottom of the viewport
        const stickyHeight = `calc(100vh - ${stickyTopOffset}px - ${stickyBottomOffset}px)`; // Full height below top offset, above bottom offset
        
        return (
            <div 
                ref={containerRef}
                className={`relative ${className}`}
                style={{ height }}
            >
                {/* Left Panel - Scrollable content */}
                <div 
                    style={{ 
                        width: `${effectiveLeftWidth}%`,
                        paddingRight: isRightPanelMinimized ? '60px' : (showResizer ? '8px' : '0'), // Add padding when minimized for button space
                        minHeight: '100vh',
                        position: 'relative'
                    }}
                >
                    {leftPanel}
                </div>

                {/* Floating Maximize Button - shown when right panel is minimized */}
                {isRightPanelMinimized && onToggleRightPanel && (
                    <div className="fixed top-3 right-3 z-50">
                        <div className="flex flex-col items-center">
                            <Button
                                onClick={handleTogglePanel}
                                className="bg-card hover:bg-muted text-foreground border border-border shadow-md hover:shadow-lg transition-all duration-200 w-6 h-6 p-0 rounded"
                                variant="outline"
                                size="sm"
                                title="Show Document"
                            >
                                <svg width="12" height="12" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <rect x="3" y="3" width="10" height="10" stroke="currentColor" strokeWidth="2" fill="none" rx="1"/>
                                </svg>
                            </Button>
                            <span className="text-xs text-muted-foreground mt-1 text-center leading-tight">Display document</span>
                        </div>
                    </div>
                )}

                {/* Resizer - Fixed position with shortened height */}
                {showResizer && (
                    <div 
                        className={`
                            fixed w-2 cursor-col-resize border border-border 
                            flex items-center justify-center transition-colors duration-200 z-40
                            ${isDragging ? 'bg-primary' : 'bg-muted hover:bg-primary/40'}
                        `}
                        style={{
                            left: `calc(${effectiveLeftWidth}% + 4px)`, // Position between left panel and PDF
                            top: `${stickyTopOffset}px`,
                            height: stickyHeight // Shortened to sit above bottom offset
                        }}
                        onMouseDown={handleMouseDown}
                        title="Drag to resize panel width"
                    >
                        {/* Visual grip indicator */}
                        <div className="flex flex-col space-y-1">
                            <div className="w-0.5 h-6 bg-foreground/70 rounded"></div>
                            <div className="w-0.5 h-6 bg-foreground/70 rounded"></div>
                        </div>
                        
                        {/* Hover area for easier grabbing */}
                        <div className="absolute inset-y-0 -left-2 -right-2"></div>
                    </div>
                )}

                {/* Right Panel - Fixed/Sticky position with offset */}
                {!isRightPanelMinimized && (
                    <div 
                        className="fixed z-50 flex flex-col border-b border-border/50"
                        style={{ 
                            left: `calc(${effectiveLeftWidth}% + 16px)`,
                            right: '60px',
                            top: `${stickyTopOffset}px`,
                            height: stickyHeight,
                            maxWidth: '800px'
                        }}
                    >
                        <div className="flex-1 bg-card border border-border rounded-lg shadow-sm p-8 flex flex-col">
                            <div 
                                className="relative flex-1 bg-muted/10 rounded overflow-hidden flex flex-col"
                                style={{ maxWidth: '100%' }}
                            >
                                {rightPanel}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    // Standard layout for fallback (if needed)
    return (
        <div 
            ref={containerRef}
            className={`flex ${className}`}
            style={{ height }}
        >
            {/* Floating Maximize Button - shown when right panel is minimized */}
            {isRightPanelMinimized && onToggleRightPanel && (
                <div className="fixed top-3 right-3 z-50">
                    <div className="flex flex-col items-center">
                        <Button
                            onClick={handleTogglePanel}
                            className="bg-card hover:bg-muted text-foreground border border-border shadow-md hover:shadow-lg transition-all duration-200 w-6 h-6 p-0 rounded"
                            variant="outline"
                            size="sm"
                            title="Show Document"
                        >
                            <svg width="12" height="12" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <rect x="3" y="3" width="10" height="10" stroke="currentColor" strokeWidth="2" fill="none" rx="1"/>
                            </svg>
                        </Button>
                        <span className="text-xs text-muted-foreground mt-1 text-center leading-tight">Display document</span>
                    </div>
                </div>
            )}

            {/* Left Panel */}
            <div 
                className="flex-shrink-0 overflow-hidden"
                style={{ 
                    width: `${effectiveLeftWidth}%`,
                    paddingRight: isRightPanelMinimized ? '60px' : '0' // Add padding when minimized for button space
                }}
            >
                {leftPanel}
            </div>

            {/* Resizer */}
            {showResizer && (
                <div 
                    className={`
                        relative flex-shrink-0 w-2 cursor-col-resize border border-border 
                        flex items-center justify-center transition-colors duration-200
                        ${isDragging ? 'bg-primary' : 'bg-muted hover:bg-primary/40'}
                    `}
                    onMouseDown={handleMouseDown}
                    title="Drag to resize panel width"
                >
                    {/* Visual grip indicator */}
                    <div className="flex flex-col space-y-1">
                        <div className="w-0.5 h-6 bg-foreground/70 rounded"></div>
                        <div className="w-0.5 h-6 bg-foreground/70 rounded"></div>
                    </div>
                    
                    {/* Hover area for easier grabbing */}
                    <div className="absolute inset-y-0 -left-2 -right-2"></div>
                </div>
            )}

            {/* Right Panel */}
            {!isRightPanelMinimized && (
                <div 
                    className="flex-1 overflow-hidden z-50"
                    style={{ width: `${effectiveRightWidth}%` }}
                >
                    {rightPanel}
                </div>
            )}
        </div>
    );
}; 